import debounce from "lodash/debounce";
import {
  type ComponentPropsWithoutRef,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { usePillarTableFilterState } from "shared/components/pillar-table/filters/hooks/usePillarTableFilterState";
import PillarTableFilterLabel from "shared/components/pillar-table/filters/PillarTableFilterLabel";
import { withoutOptionSourceCollection } from "shared/components/pillar-table/filters/PillarTableFilterOptionSources";

type PillarTableFilterTextInputProps = Omit<
  ComponentPropsWithoutRef<"input">,
  "onChange" | "value" | "type" | "name" | "title"
> & {
  title: string;
  filterKey: string;
  testId?: string;
  toolTip?: ReactNode;
};

const PillarTableFilterTextInput = ({
  filterKey,
  title,
  testId,
  toolTip,
  ...props
}: PillarTableFilterTextInputProps) => {
  const { filterValue, setFilterValue } = usePillarTableFilterState<string>({
    filterKey,
    defaultValue: props.defaultValue?.toString(),
  });

  const [inputValue, setInputValue] = useState<string>(filterValue ?? "");

  // One debounced setter per component instance so multiple text filters on
  // the same page don't share (and clobber) a single module-scoped timer.
  // `setFilterValue` is passed in at call time so the trailing invocation
  // always uses the latest setter.
  const debouncedSetFilterValue = useMemo(
    () =>
      debounce(
        (value: string, setter: (next: string) => void) => {
          setter(value);
        },
        1000,
        { trailing: true, leading: false },
      ),
    [],
  );

  useEffect(
    () => () => {
      debouncedSetFilterValue.cancel();
    },
    [debouncedSetFilterValue],
  );

  useEffect(() => {
    setInputValue(filterValue ?? "");
  }, [filterValue]);

  useEffect(() => {
    if (filterValue !== inputValue) {
      debouncedSetFilterValue(inputValue, setFilterValue);
    }
    // We intentionally do NOT depend on `setFilterValue` here. The hook
    // returns a fresh function identity on every parent render, which would
    // re-run this effect and reset the debounce timer before it could fire
    // (causing the filter API call to never go out). The latest setter is
    // still used because we pass it in at call time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, filterValue]);

  return (
    <div className="flex flex-col justify-start items-start">
      <PillarTableFilterLabel
        className="text-filter-label"
        htmlFor={`filter-text-input-${filterKey}`}
        filterKey={testId ?? filterKey}
        title={title}
        toolTip={toolTip}
      />
      <input
        name={`filter-text-input-${filterKey}`}
        data-testid={`filter-text-input-${testId ?? filterKey}`}
        title={title}
        value={inputValue}
        type="text"
        onChange={(e) => {
          setInputValue(e.target.value);
        }}
        {...props}
      />
    </div>
  );
};

export default withoutOptionSourceCollection(PillarTableFilterTextInput);
